import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle } from './ui/card';
import { 
  FileText,
  ShoppingCart,
  RefreshCw,
  Users,
  FileEdit,
  QrCode,
  Package,
  ClipboardCheck,
  Truck,
  Receipt,
  CreditCard
} from 'lucide-react';
import InventorySection from './InventorySection';
import CustomerPage from './CustomerPage';
import SupplierPage from './SupplierPage';
import RefundsScreen from './RefundsScreen';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { TRANSACTION_CARD_PRIVILEGE } from '@/lib/privilegeConstants';

interface TransactionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

const TransactionCard = ({ icon, title, description, onClick }: TransactionCardProps) => (
  <Card 
    className="group cursor-pointer bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 min-h-[120px] sm:min-h-[140px] md:min-h-[160px] lg:min-h-[90px] xl:min-h-[100px] 2xl:min-h-[160px] active:scale-95 active:shadow-lg"
    onClick={onClick}
  >
    <CardHeader className="flex flex-row items-start justify-between p-2 sm:p-3 md:p-5 lg:p-2 xl:p-2.5 2xl:p-6 pb-2 sm:pb-3 lg:pb-2 xl:pb-2.5 2xl:pb-4 space-y-0">
      <div className="space-y-0.5 sm:space-y-1 md:space-y-2 lg:space-y-0.5 xl:space-y-0.5 2xl:space-y-2 flex-1 pr-1.5 lg:pr-1.5 xl:pr-2">
        <CardTitle className="text-sm sm:text-base md:text-lg lg:text-xs xl:text-xs 2xl:text-lg font-semibold text-gray-900 leading-tight">
          {title}
        </CardTitle>
        <p className="text-xs sm:text-sm md:text-base lg:text-[10px] xl:text-[11px] 2xl:text-base text-gray-500 leading-tight">
          {description}
        </p>
      </div>
      <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-7 lg:h-7 xl:w-8 xl:h-8 2xl:w-12 2xl:h-12 rounded-lg bg-blue-500 flex items-center justify-center text-white flex-shrink-0">
        <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4 2xl:w-6 2xl:h-6">
          {icon}
        </div>
      </div>
    </CardHeader>
  </Card>
);

interface TransactionsTabProps {
  onSalesClick?: () => void;
  token: string;
  onSubpageChange?: (subpage: string | null) => void;
}

const TransactionsTab = ({ onSalesClick, token, onSubpageChange }: TransactionsTabProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPrivilege, loading: privilegesLoading, privilegeIds } = useUserPrivileges();
  const [showInventory, setShowInventory] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [showRefunds, setShowRefunds] = useState(false);

  // Notify Dashboard when entering/leaving a subpage so header can be hidden
  const setSubpage = (isSubpage: boolean) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('transactionsSubpageChange', { detail: { isSubpage } }));
    }
  };

  useEffect(() => {
    const anySubpage = showInventory || showCustomers || showSuppliers || showRefunds;
    setSubpage(anySubpage);
    
    // Notify Dashboard about subpage changes
    if (onSubpageChange) {
      if (showInventory) {
        onSubpageChange('inventory');
      } else if (showCustomers) {
        onSubpageChange('customers');
      } else if (showSuppliers) {
        onSubpageChange('suppliers');
      } else if (showRefunds) {
        onSubpageChange('refunds');
      } else {
        onSubpageChange(null);
      }
    }
    
    return () => {
      // Ensure header is restored if component unmounts
      setSubpage(false);
      if (onSubpageChange) {
        onSubpageChange(null);
      }
    };
  }, [showInventory, showCustomers, showSuppliers, showRefunds, onSubpageChange]);

  // Handle showRefunds state from ReturnProcessPage navigation
  useEffect(() => {
    if (location.state?.showRefunds) {
      console.log('🔄 TransactionsTab: Showing refunds screen from navigation state');
      setShowRefunds(true);
      // Clear state via React Router so it doesn't persist when navigating away and back.
      // window.history.replaceState does NOT update React Router's location, so returning
      // to Transactions would incorrectly show Refunds again.
      navigate(location.pathname + location.search, { replace: true, state: {} });
      
      // Trigger refresh if requested
      if (location.state?.refresh) {
        console.log('🔄 TransactionsTab: Triggering refunds data refresh');
        window.dispatchEvent(new CustomEvent('refreshRefunds'));
      }
    }
  }, [location.state, location.pathname, location.search, navigate]);

  // Listen for reset event when user clicks Transactions tab in sidebar (return to main grid from subpage)
  useEffect(() => {
    const handleResetToMain = () => {
      setShowInventory(false);
      setShowCustomers(false);
      setShowSuppliers(false);
      setShowRefunds(false);
    };
    window.addEventListener('resetTransactionsToMain', handleResetToMain);
    return () => window.removeEventListener('resetTransactionsToMain', handleResetToMain);
  }, []);

  // Listen for custom showRefunds event from ReturnProcessPage
  useEffect(() => {
    const handleShowRefunds = (event: CustomEvent) => {
      console.log('🔄 TransactionsTab: Received showRefunds event', event.detail);
      console.log('🔄 TransactionsTab: Current showRefunds state:', showRefunds);
      setShowRefunds(true);
      console.log('🔄 TransactionsTab: Set showRefunds to true');
      
      // Trigger refresh if requested
      if (event.detail?.refresh) {
        console.log('🔄 TransactionsTab: Triggering refunds data refresh from event');
        window.dispatchEvent(new CustomEvent('refreshRefunds'));
      }
    };

    console.log('🔄 TransactionsTab: Adding showRefunds event listener');
    window.addEventListener('showRefunds', handleShowRefunds);
    return () => {
      console.log('🔄 TransactionsTab: Removing showRefunds event listener');
      window.removeEventListener('showRefunds', handleShowRefunds);
    };
  }, []);

  // When coming back from UploadImage or other routes, auto-open Inventory if requested
  useEffect(() => {
    const state: any = location.state || {};
    const openInventoryFlag =
      state.openInventory || sessionStorage.getItem('openInventory') === 'true';

    if (openInventoryFlag) {
      setShowInventory(true);
      sessionStorage.removeItem('openInventory');
    }
  }, [location.state]);

  // Tabs hidden until functionality is ready (desktop and mobile)
  const HIDDEN_TRANSACTION_TITLES = [
    "Quick Invoice",
    "Expense Vouchers",
    "Barcodes",
    "Quote/Estimate",
    "Stock Verifications",
    "Delivery Challan",
    "Bank Transactions",
  ];

  const transactionCards = [
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Sales Invoice",
      description: "Create and manage sales invoices",
      onClick: onSalesClick || (() => {})
    },
    {
      icon: <ShoppingCart className="w-5 h-5" />,
      title: "Quick Invoice",
      description: "Generate invoices quickly",
      onClick: () => {}
    },
    {
      icon: <RefreshCw className="w-5 h-5" />,
      title: "Refunds",
      description: "Process customer refunds",
      onClick: () => setShowRefunds(true)
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "Customers",
      description: "Manage customer information",
      onClick: () => setShowCustomers(true)
    },
    {
      icon: <Receipt className="w-5 h-5" />,
      title: "Expense Vouchers",
      description: "Record business expenses",
      onClick: () => {}
    },
    {
      icon: <QrCode className="w-5 h-5" />,
      title: "Barcodes",
      description: "Generate and manage barcodes",
      onClick: () => {}
    },
    {
      icon: <FileEdit className="w-5 h-5" />,
      title: "Quote/Estimate",
      description: "Create quotes and estimates",
      onClick: () => {}
    },
    {
      icon: <Package className="w-5 h-5" />,
      title: "Inventory",
      description: "Manage inventory levels",
      onClick: () => setShowInventory(true)
    },
    {
      icon: <Truck className="w-5 h-5" />,
      title: "Purchases/Supplier",
      description: "Handle supplier purchases",
      onClick: () => setShowSuppliers(true)
    },
    {
      icon: <ClipboardCheck className="w-5 h-5" />,
      title: "Stock Verifications",
      description: "Verify stock quantities",
      onClick: () => {}
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Delivery Challan",
      description: "Manage delivery documents",
      onClick: () => {}
    },
    {
      icon: <CreditCard className="w-5 h-5" />,
      title: "Bank Transactions",
      description: "Record bank transactions",
      onClick: () => {}
    }
  ].filter((card) => !HIDDEN_TRANSACTION_TITLES.includes(card.title));

  // Filter cards by user privileges; when loading or no privileges, show all (avoid blank screen)
  const visibleTransactionCards = privilegesLoading || privilegeIds.size === 0
    ? transactionCards
    : transactionCards.filter((card) => {
        const requiredPrevilageId = TRANSACTION_CARD_PRIVILEGE[card.title];
        if (requiredPrevilageId == null) return true;
        return hasPrivilege(requiredPrevilageId);
      });

  if (showInventory) {
    return <InventorySection token={token} onBack={() => { setShowInventory(false); }} />;
  }

  if (showCustomers) {
    return <CustomerPage token={token} onBack={() => { setShowCustomers(false); }} />;
  }

  if (showSuppliers) {
    return <SupplierPage token={token} onBack={() => { setShowSuppliers(false); }} />;
  }

  if (showRefunds) {
    return <RefundsScreen token={token} onBack={() => { setShowRefunds(false); }} />;
  }

  return (
    <div className="bg-white rounded-lg p-2 sm:p-3 md:p-4 lg:p-2 xl:p-3 2xl:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 lg:gap-1.5 xl:gap-2 2xl:gap-4 mb-1.5 sm:mb-3 md:mb-4 lg:mb-1.5 xl:mb-2 2xl:mb-4">
        <p className="hidden md:block text-sm sm:text-base lg:text-[10px] xl:text-xs 2xl:text-base text-gray-500">Select a transaction type to proceed</p>
      </div>
      <div className="grid grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-2 xl:gap-2.5 2xl:gap-4">
        {visibleTransactionCards.map((card, index) => (
          <TransactionCard key={index} {...card} />
        ))}
      </div>
    </div>
  );
};

export default TransactionsTab;
